// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Aircraft review settings dialog: per-aircraft profile lookup
// helpers, monitor group counts + dialog active counter, summary
// profile chip, group enable/mute action, status display, save-button
// reset/cleanup, the dialog renderer (groups + monitor checkboxes),
// open/close, the cache invalidation that runs on save (clears
// detection-window / scoped-health / raw analysis / flight-flag
// caches for the aircraft), the save handler, and the in-dialog reset.
//
// Exposed as a factory because the cluster reads/writes the
// aircraftReviewSettings + close-timer + five health/detection cache
// globals and depends on many late-bound app.js helpers.

export function createAircraftReviewSettingsDialog({
  escapeHtml,
  appReadOnlyMode,
  readOnlyActionMessage,
  readOnlyPreviewMessage,
  syncReadOnlyUi,
  aircraftDisplayLabel,
  aircraftProfileForEntry,
  aircraftProfileSupportsMultiEngineReview,
  groupedReviewMonitorDefinitionsForProfile,
  reviewMonitorDefinitionsForProfile,
  reviewMonitorCountTextForAircraft,
  coolingRateTriggerSettingForAircraft,
  normalizeCoolingRateTriggerSetting,
  coolingRateTriggerSettingBounds,
  aircraftMonitorCapability,
  disabledReviewMonitorKeys,
  updateAircraftReviewMonitorSettings,
  saveAircraftReviewSettings,
  selectedAircraftWorkspace,
  aircraftFlights,
  flightFlagSummaryCache,
  aircraftFlightFlagSummaryIndexPromises,
  clearPersistedAircraftFlightFlagSummaries,
  rawAnalysisCache,
  clearTrendRowsForAircraft,
  reapplyDashboardAnalysis,
  renderAircraftWorkspace,
  getAircraftProfiles,
  getAircraftReviewSettings,
  setAircraftReviewSettings,
  getAircraftReviewSettingsCloseTimer,
  setAircraftReviewSettingsCloseTimer,
  setSelectedDetectionWindowCache,
  setPlaybackDetectionWindowCache,
  setScopedHealthSummaryCache,
  setRawScopedAnalysisCache,
  getSelectedFlight,
  getFlightData,
  getHealthSummary,
  fetchAircraftMonitorCapabilitySummary,
  onCloseAircraftReviewSettingsDialog,
}) {
  // Server-sourced capability evidence per aircraft (the latest run's metric-keyed
  // score_summary), so monitors can be marked unavailable even when no flight for
  // the aircraft is loaded. A null entry means "fetched, nothing usable".
  const capabilitySummaryByAircraft = new Map();

  function reviewSettingsProfileForAircraft(aircraft = selectedAircraftWorkspace()) {
    return aircraftProfileForEntry({ aircraft }) ?? getAircraftProfiles()?.[aircraft] ?? null;
  }

  // Evidence for monitor capability: the loaded flight's health summary when it
  // belongs to this aircraft (instant, full fidelity) plus the fetched latest-run
  // summary. Channel availability is a property of the engine monitor (constant
  // across an aircraft's logs), so either witness is reliable; with neither, every
  // monitor stays available (the honest default).
  function reviewSettingsEvidenceSummaries(aircraft = selectedAircraftWorkspace()) {
    const summaries = [];
    const loaded = getHealthSummary?.();
    if (loaded && getSelectedFlight?.()?.aircraft === aircraft) summaries.push(loaded);
    const fetched = capabilitySummaryByAircraft.get(aircraft);
    if (fetched) summaries.push(fetched);
    return summaries;
  }

  // Fetch (once per aircraft per session) the latest analyzed run so capability
  // is known without a loaded flight. Cached negative results avoid refetching.
  async function ensureAircraftCapabilitySummary(aircraft = selectedAircraftWorkspace()) {
    if (!aircraft || capabilitySummaryByAircraft.has(aircraft)) return;
    let summary = null;
    try {
      summary = (await fetchAircraftMonitorCapabilitySummary?.(aircraft)) ?? null;
    } catch {
      summary = null;
    }
    if (!capabilitySummaryByAircraft.has(aircraft)) capabilitySummaryByAircraft.set(aircraft, summary);
  }

  function reviewMonitorCapability(aircraft = selectedAircraftWorkspace()) {
    return aircraftMonitorCapability(
      reviewSettingsEvidenceSummaries(aircraft),
      reviewSettingsProfileForAircraft(aircraft),
    );
  }

  function groupedReviewMonitorDefinitions(aircraft = selectedAircraftWorkspace()) {
    return groupedReviewMonitorDefinitionsForProfile(reviewSettingsProfileForAircraft(aircraft));
  }

  function reviewMonitorDefinitions(aircraft = selectedAircraftWorkspace()) {
    return reviewMonitorDefinitionsForProfile(reviewSettingsProfileForAircraft(aircraft));
  }

  function reviewMonitorCountText(aircraft = selectedAircraftWorkspace()) {
    return reviewMonitorCountTextForAircraft(getAircraftReviewSettings(), aircraft, reviewSettingsProfileForAircraft(aircraft));
  }

  function reviewMonitorGroupKey(label = "") {
    return String(label || "group")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "group";
  }

  function reviewMonitorInputsIn(root) {
    return Array.from(root?.querySelectorAll?.("[data-review-monitor-key]") ?? []);
  }

  // Unavailable monitors are shown but cannot toggle and never fire, so they are
  // excluded from the active/total tally and counted on their own line. Group
  // and reset actions also skip them via this filter.
  function availableReviewMonitorInputsIn(root) {
    return reviewMonitorInputsIn(root).filter((input) => !input.dataset.reviewMonitorUnavailable);
  }

  function coolingRateTriggerInput(form = document.querySelector("#aircraftReviewSettingsForm")) {
    return form?.querySelector?.("[data-cooling-rate-trigger-input]") ?? null;
  }

  function coolingRateMonitorInput(form = document.querySelector("#aircraftReviewSettingsForm")) {
    return form?.querySelector?.('[data-review-monitor-key="coolingRate"]') ?? null;
  }

  function syncCoolingRateTriggerInputState(form = document.querySelector("#aircraftReviewSettingsForm")) {
    const triggerInput = coolingRateTriggerInput(form);
    const monitorInput = coolingRateMonitorInput(form);
    if (!triggerInput || !monitorInput) return;
    const disabled = Boolean(monitorInput.disabled || !monitorInput.checked);
    triggerInput.disabled = disabled;
    triggerInput.closest?.("[data-cooling-rate-trigger-setting]")?.classList?.toggle("is-muted", disabled);
  }

  function reviewMonitorActiveText(active, total, unavailable = 0) {
    if (!total) return unavailable ? `${unavailable} unavailable` : "No monitors";
    const base = active === total ? `${total} active` : `${active} of ${total} active`;
    return unavailable ? `${base} · ${unavailable} unavailable` : base;
  }

  function updateReviewSettingsGroupCounts(form = document.querySelector("#aircraftReviewSettingsForm")) {
    form?.querySelectorAll?.("[data-review-monitor-group]").forEach((groupElement) => {
      const available = availableReviewMonitorInputsIn(groupElement);
      const active = available.filter((input) => input.checked).length;
      const unavailable = reviewMonitorInputsIn(groupElement).length - available.length;
      const count = groupElement.querySelector?.("[data-review-group-count]");
      if (count) count.textContent = reviewMonitorActiveText(active, available.length, unavailable);
    });
  }

  function updateReviewSettingsDialogCount() {
    const count = document.querySelector("#aircraftReviewSettingsCount");
    const mutedCount = document.querySelector("#aircraftReviewSettingsMutedCount");
    const unavailableCount = document.querySelector("#aircraftReviewSettingsUnavailableCount");
    const form = document.querySelector("#aircraftReviewSettingsForm");
    if (!form) return;
    const available = availableReviewMonitorInputsIn(form);
    const active = available.filter((input) => input.checked).length;
    const total = available.length;
    const muted = Math.max(0, total - active);
    const unavailable = reviewMonitorInputsIn(form).length - total;
    if (count) count.textContent = total ? `${active} of ${total}` : "No monitors";
    if (mutedCount) mutedCount.textContent = total ? String(muted) : "--";
    if (unavailableCount) unavailableCount.textContent = String(unavailable);
    updateReviewSettingsGroupCounts(form);
    syncCoolingRateTriggerInputState(form);
  }

  function updateReviewSettingsSummaryProfile(aircraft = selectedAircraftWorkspace()) {
    const profileSummary = document.querySelector("#aircraftReviewSettingsProfile");
    if (!profileSummary) return;
    const profile = reviewSettingsProfileForAircraft(aircraft);
    profileSummary.textContent = aircraftProfileSupportsMultiEngineReview(profile) ? "Multi-engine" : "Single engine";
  }

  function applyReviewSettingsGroupAction(button) {
    const group = button?.closest?.("[data-review-monitor-group]");
    if (!group) return;
    const shouldEnable = button.dataset.reviewGroupAction === "enable";
    availableReviewMonitorInputsIn(group).forEach((input) => {
      input.checked = shouldEnable;
    });
    updateReviewSettingsDialogCount();
    setAircraftReviewSettingsStatus(
      shouldEnable ? "Group monitors enabled." : "Group monitors muted.",
      "info",
    );
  }

  function setAircraftReviewSettingsStatus(message = "", tone = "info") {
    const status = document.querySelector("#aircraftReviewSettingsStatus");
    if (!status) return;
    status.textContent = message;
    status.dataset.tone = tone;
    status.classList.toggle("is-active", Boolean(message));
  }

  function resetAircraftReviewSettingsSaveState() {
    const timer = getAircraftReviewSettingsCloseTimer();
    if (timer) {
      clearTimeout(timer);
      setAircraftReviewSettingsCloseTimer(null);
    }
    const saveButton = document.querySelector("#aircraftReviewSettingsSave");
    if (saveButton) {
      saveButton.disabled = appReadOnlyMode();
      saveButton.textContent = "Save Settings";
      saveButton.title = appReadOnlyMode() ? readOnlyActionMessage("Saving review settings") : "";
    }
  }

  function renderAircraftReviewSettingsDialog(aircraft = selectedAircraftWorkspace()) {
    const title = document.querySelector("#aircraftReviewSettingsTitle");
    const copy = document.querySelector("#aircraftReviewSettingsCopy");
    const groups = document.querySelector("#aircraftReviewMonitorGroups");
    if (!groups) return;
    const disabledKeys = disabledReviewMonitorKeys(aircraft);
    const capability = reviewMonitorCapability(aircraft);
    const aircraftLabel = aircraftDisplayLabel(aircraft) || "this aircraft";
    const coolingRateBounds = coolingRateTriggerSettingBounds();
    const coolingRateTrigger = coolingRateTriggerSettingForAircraft(getAircraftReviewSettings(), aircraft);
    if (title) title.textContent = `${aircraftLabel} review settings`;
    if (copy) copy.textContent = "Choose which automated review monitors can create flags, review cards, timeline markers, and score penalties where applicable for this aircraft.";
    groups.innerHTML = groupedReviewMonitorDefinitions(aircraft).map((group) => `
      <section class="review-monitor-group" data-review-monitor-group="${escapeHtml(reviewMonitorGroupKey(group.label))}">
        <header class="review-monitor-group-header">
          <div>
            <h3>${escapeHtml(group.label)}</h3>
            <span data-review-group-count></span>
          </div>
          <div class="review-monitor-group-actions">
            <button class="secondary-action review-monitor-group-action" data-review-group-action="enable" type="button">Enable group</button>
            <button class="secondary-action review-monitor-group-action" data-review-group-action="mute" type="button">Mute group</button>
          </div>
        </header>
        <div class="review-monitor-list">
          ${group.monitors.map((monitor) => {
            const unavailable = capability.get(monitor.key)?.available === false;
            const reason = capability.get(monitor.key)?.reason || "";
            const checked = !unavailable && !disabledKeys.has(monitor.key);
            const inputId = `review-monitor-${escapeHtml(reviewMonitorGroupKey(`${group.label}-${monitor.key}`))}`;
            const coolingRateSetting = monitor.key === "coolingRate" && !unavailable
              ? `
                <div class="review-monitor-setting" data-cooling-rate-trigger-setting>
                  <label for="coolingRateTriggerFpmInput">Cooling rate trigger</label>
                  <div class="review-monitor-number-field">
                    <input id="coolingRateTriggerFpmInput" data-cooling-rate-trigger-input type="number" inputmode="numeric" min="${coolingRateBounds.minFpm}" max="${coolingRateBounds.maxFpm}" step="1" value="${escapeHtml(coolingRateTrigger)}" autocomplete="off" />
                    <span>F/min</span>
                  </div>
                  <small>Range ${coolingRateBounds.minFpm}-${coolingRateBounds.maxFpm}; default ${coolingRateBounds.defaultFpm}.</small>
                </div>
              `
              : "";
            return `
              <div class="review-monitor-option${unavailable ? " is-unavailable" : ""}">
                <input id="${inputId}" data-review-monitor-key="${escapeHtml(monitor.key)}"${unavailable ? ` data-review-monitor-unavailable="1" disabled title="${escapeHtml(reason)}"` : ""} type="checkbox"${checked ? " checked" : ""} />
                <label class="review-monitor-option-copy" for="${inputId}">
                  <span>${escapeHtml(monitor.label)}</span>
                  <small>${escapeHtml(unavailable ? reason : monitor.description)}</small>
                  ${unavailable ? `<small class="review-monitor-unavailable-tag">Unavailable on this aircraft</small>` : ""}
                </label>
                ${coolingRateSetting}
              </div>
            `;
          }).join("")}
        </div>
      </section>
    `).join("");
    updateReviewSettingsSummaryProfile(aircraft);
    updateReviewSettingsDialogCount();
  }

  async function openAircraftReviewSettingsDialog(aircraft = selectedAircraftWorkspace()) {
    const dialog = document.querySelector("#aircraftReviewSettingsDialog");
    if (!dialog || !aircraft) return;
    dialog.dataset.aircraft = aircraft;
    renderAircraftReviewSettingsDialog(aircraft);
    resetAircraftReviewSettingsSaveState();
    setAircraftReviewSettingsStatus(appReadOnlyMode() ? readOnlyPreviewMessage() : "");
    syncReadOnlyUi();
    document.querySelector("#aircraftReviewSettingsForm")?.scrollTo?.({ top: 0, left: 0 });
    // Inline mode (aircraft page Settings tab): the dialog is reparented into
    // the tab panel and shown non-modally via the open attribute.
    if (dialog.classList?.contains?.("is-inline-panel")) dialog.setAttribute("open", "");
    else if (typeof dialog.showModal === "function") dialog.showModal();
    // Broaden capability from the server (latest analyzed run), then re-render so
    // monitors this aircraft cannot feed are marked even with no flight loaded.
    // Skip the re-render if the user switched aircraft or closed the dialog while
    // the fetch was in flight. Re-rendering rebuilds checkboxes from saved state,
    // which is correct here: the only thing that changes is the just-opened view.
    if (!capabilitySummaryByAircraft.has(aircraft)) {
      await ensureAircraftCapabilitySummary(aircraft);
      if (dialog.dataset.aircraft === aircraft && (dialog.open ?? true)) {
        renderAircraftReviewSettingsDialog(aircraft);
        resetAircraftReviewSettingsSaveState();
      }
    }
  }

  function closeAircraftReviewSettingsDialog() {
    resetAircraftReviewSettingsSaveState();
    const dialog = document.querySelector("#aircraftReviewSettingsDialog");
    if (dialog?.classList?.contains?.("is-inline-panel")) {
      // Inline (Settings tab): "close" (save done, or Cancel) re-renders from
      // saved state instead of blanking the panel.
      const aircraft = dialog.dataset.aircraft || undefined;
      openAircraftReviewSettingsDialog(aircraft);
      onCloseAircraftReviewSettingsDialog?.();
      return;
    }
    dialog?.close();
    onCloseAircraftReviewSettingsDialog?.();
  }

  function resetReviewPreferenceCaches(aircraft) {
    setSelectedDetectionWindowCache({ summary: null, key: "", windows: [] });
    setPlaybackDetectionWindowCache({ summary: null, windows: [] });
    setScopedHealthSummaryCache({ source: null, data: null, sensitivity: "", reviewSettings: "", summaries: new Map() });
    rawAnalysisCache.clear();
    setRawScopedAnalysisCache({ data: null, signature: "", bundles: new Map() });
    aircraftFlights(aircraft).forEach((entry) => {
      if (entry?.id) flightFlagSummaryCache.delete(entry.id);
    });
    aircraftFlights(aircraft)
      .map((entry) => entry?.database?.aircraftId)
      .filter(Boolean)
      .forEach((aircraftId) => {
        aircraftFlightFlagSummaryIndexPromises.delete(aircraftId);
        clearPersistedAircraftFlightFlagSummaries?.(aircraftId);
      });
    clearTrendRowsForAircraft(aircraft);
  }

  function saveAircraftReviewSettingsFromDialog() {
    const dialog = document.querySelector("#aircraftReviewSettingsDialog");
    const form = document.querySelector("#aircraftReviewSettingsForm");
    const aircraft = dialog?.dataset.aircraft || selectedAircraftWorkspace();
    if (!form || !aircraft) return;
    if (appReadOnlyMode()) {
      setAircraftReviewSettingsStatus(readOnlyActionMessage("Saving review settings"), "info");
      syncReadOnlyUi();
      return;
    }
    const disabledKeys = availableReviewMonitorInputsIn(form)
      .filter((input) => !input.checked)
      .map((input) => input.dataset.reviewMonitorKey)
      .filter(Boolean);
    const coolingRateTriggerFpm = normalizeCoolingRateTriggerSetting(coolingRateTriggerInput(form)?.value);
    const updated = updateAircraftReviewMonitorSettings(getAircraftReviewSettings(), aircraft, disabledKeys, { coolingRateTriggerFpm });
    setAircraftReviewSettings(updated);
    saveAircraftReviewSettings(updated);
    resetReviewPreferenceCaches(aircraft);
    const selectedFlight = getSelectedFlight();
    const flightData = getFlightData();
    if (selectedFlight?.aircraft === aircraft && flightData?.length) {
      // Refresh the dashboard for the new monitor set off the main thread: the
      // change re-keys the analysis cache, so a synchronous recompute would
      // freeze the UI for seconds. Fire-and-forget (it self-guards against a
      // flight switch) so the dialog can show "Saved" and close immediately.
      reapplyDashboardAnalysis();
    }
    renderAircraftWorkspace();
    setAircraftReviewSettingsStatus("Settings saved.", "success");
    // Inline (Settings tab) autosave: no Save button, no close; the panel
    // stays put with its checkboxes as the live state. (Optional chaining all
    // the way down: test harnesses stub elements without a real classList.)
    if (dialog?.classList?.contains?.("is-inline-panel")) return;
    const saveButton = document.querySelector("#aircraftReviewSettingsSave");
    if (saveButton) {
      saveButton.disabled = true;
      saveButton.textContent = "Saved";
    }
    setAircraftReviewSettingsCloseTimer(setTimeout(() => {
      setAircraftReviewSettingsCloseTimer(null);
      closeAircraftReviewSettingsDialog();
    }, 850));
  }

  function resetAircraftReviewSettingsInDialog() {
    const form = document.querySelector("#aircraftReviewSettingsForm");
    if (!form) return;
    availableReviewMonitorInputsIn(form).forEach((input) => {
      input.checked = true;
    });
    const bounds = coolingRateTriggerSettingBounds();
    const triggerInput = coolingRateTriggerInput(form);
    if (triggerInput) triggerInput.value = String(bounds.defaultFpm);
    updateReviewSettingsDialogCount();
    setAircraftReviewSettingsStatus("All review monitors restored.", "info");
  }

  return {
    reviewSettingsProfileForAircraft,
    reviewSettingsEvidenceSummaries,
    ensureAircraftCapabilitySummary,
    reviewMonitorCapability,
    groupedReviewMonitorDefinitions,
    reviewMonitorDefinitions,
    reviewMonitorCountText,
    reviewMonitorGroupKey,
    reviewMonitorInputsIn,
    availableReviewMonitorInputsIn,
    reviewMonitorActiveText,
    updateReviewSettingsGroupCounts,
    updateReviewSettingsDialogCount,
    updateReviewSettingsSummaryProfile,
    applyReviewSettingsGroupAction,
    setAircraftReviewSettingsStatus,
    resetAircraftReviewSettingsSaveState,
    renderAircraftReviewSettingsDialog,
    openAircraftReviewSettingsDialog,
    closeAircraftReviewSettingsDialog,
    resetReviewPreferenceCaches,
    saveAircraftReviewSettingsFromDialog,
    resetAircraftReviewSettingsInDialog,
  };
}
